import { OnboardingStepId, OnboardingProgress, OnboardingData } from '../../types/onboarding';

const STORAGE_KEY = 'onboarding_progress';
const COMPLETION_KEY = 'onboarding_completed';
const PREFILLED_KEY = 'onboarding_profile_prefilled';

class OnboardingStorageService {
  private getStorageKey(stepId?: OnboardingStepId): string {
    return stepId ? `${STORAGE_KEY}_${stepId}` : STORAGE_KEY;
  }

  saveProgress(stepId: OnboardingStepId, data: any): void {
    try {
      const key = this.getStorageKey(stepId);
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving onboarding progress:', error);
    }
  }

  getStepData(stepId: OnboardingStepId): any {
    try {
      const key = this.getStorageKey(stepId);
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error getting step data:', error);
      return null;
    }
  }

  getProgress(): OnboardingProgress | null {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) {
        return {
          currentStep: 'welcome',
          completedSteps: [],
          skippedSteps: [],
          stepData: {}
        };
      }
      return JSON.parse(data);
    } catch (error) {
      console.error('Error getting onboarding progress:', error);
      return {
        currentStep: 'welcome',
        completedSteps: [],
        skippedSteps: [],
        stepData: {}
      };
    }
  }

  saveProgressState(progress: OnboardingProgress): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    } catch (error) {
      console.error('Error saving progress state:', error);
    }
  }

  markStepComplete(stepId: OnboardingStepId): void {
    const progress = this.getProgress();
    if (progress) {
      if (!progress.completedSteps.includes(stepId)) {
        progress.completedSteps.push(stepId);
      }
      progress.skippedSteps = progress.skippedSteps.filter(id => id !== stepId);
      this.saveProgressState(progress);
    }
  }

  markStepSkipped(stepId: OnboardingStepId): void {
    const progress = this.getProgress();
    if (progress) {
      if (!progress.skippedSteps.includes(stepId)) {
        progress.skippedSteps.push(stepId);
      }
      progress.completedSteps = progress.completedSteps.filter(id => id !== stepId);
      this.saveProgressState(progress);
    }
  }

  setCurrentStep(stepId: OnboardingStepId): void {
    const progress = this.getProgress();
    if (progress) {
      progress.currentStep = stepId;
      this.saveProgressState(progress);
    }
  }

  completeOnboarding(): void {
    try {
      localStorage.setItem(COMPLETION_KEY, 'true');
      const progress = this.getProgress();
      if (progress) {
        progress.currentStep = 'completion';
        this.saveProgressState(progress);
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  }

  // Lets a restarted onboarding run (e.g. re-opened from the profile menu)
  // re-sync from the latest Edit Profile data, in case it changed since the
  // last onboarding attempt.
  clearPrefillFlag(): void {
    try {
      localStorage.removeItem(PREFILLED_KEY);
    } catch (error) {
      console.error('Error clearing onboarding prefill flag:', error);
    }
  }

  isOnboardingComplete(): boolean {
    try {
      const completed = localStorage.getItem(COMPLETION_KEY);
      return completed === 'true';
    } catch (error) {
      console.error('Error checking onboarding completion:', error);
      return false;
    }
  }

  clearOnboardingData(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(COMPLETION_KEY);
      localStorage.removeItem(PREFILLED_KEY);
      // Clear all step-specific data
      const stepIds: OnboardingStepId[] = ['welcome', 'assessment', 'persona-confirmation', 'photos', 'profile', 'interests', 'preferences', 'app-tour', 'completion'];
      stepIds.forEach(stepId => {
        localStorage.removeItem(this.getStorageKey(stepId));
      });
    } catch (error) {
      console.error('Error clearing onboarding data:', error);
    }
  }

  getAllOnboardingData(): OnboardingData {
    const assessmentData = this.getStepData('assessment') || {};
    const personaConfirmationData = this.getStepData('persona-confirmation') || {};
    const profileData = this.getStepData('profile') || {};

    return {
      persona: {
        selectedPersona: assessmentData.selectedPersona,
        personaTraits: assessmentData.personaTraits,
        // These are answered/saved on the persona-confirmation step, not assessment.
        personaResponses: personaConfirmationData.personaResponses,
        personaConfidence: personaConfirmationData.personaConfidence
      },
      photos: this.getStepData('photos')?.photos || [],
      bio: profileData.bio || { content: '', characterCount: 0 },
      occupation: profileData.occupation || '',
      education: profileData.education || '',
      location: profileData.location || { city: '', state: '', country: '' },
      interests: this.getStepData('interests')?.interests || [],
      preferences: this.getStepData('preferences')?.preferences || {
        ageRange: { min: 18, max: 99 },
        maxDistance: 50,
        showMe: []
      },
      appTourViewed: this.getStepData('app-tour')?.appTourViewed || false
    };
  }
}

export const onboardingStorage = new OnboardingStorageService();

